import nativeBridge from '@/services/nativeBridge'
import { mobileApiClient } from '@/utils/api'

/**
 * File Download Utility
 * Handles both Firebase URLs and local API downloads
 */
class FileDownloadUtil {
  constructor() {
    // Remove static Flutter check - will check dynamically
  }

  /**
   * Check if URL is a Firebase URL
   */
  isFirebaseUrl(url) {
    return url && (url.includes('firebasestorage.app') || url.includes('googleapis.com'))
  }

  /**
   * Check if URL is a local API URL
   */
  isLocalApiUrl(url) {
    return url && !url.startsWith('http')
  }

  /**
   * Download file using native bridge or web fallback
   */
  async downloadFile(documentInfo) {
    try {
      console.log('=== DOWNLOAD DEBUG START ===')
      console.log('Document Info:', documentInfo)
      console.log('Downloading file:', documentInfo.Docname, 'URL:', documentInfo.FileUrl)
      console.log('Storage Type:', documentInfo.storagetype)
      
      // Check if we're in Flutter app dynamically with better debugging
      console.log('🔧 Checking Flutter bridge...')
      console.log('🔧 window.flutterBridge:', !!window.flutterBridge)
      console.log('🔧 window.Flutter:', !!window.Flutter)
      console.log('🔧 window.flutterBridge methods:', Object.keys(window.flutterBridge || {}))
      
      const isFlutter = nativeBridge.isRunningInFlutter()
      console.log('🔧 Is Flutter app (from nativeBridge):', isFlutter)
      
      // Additional check for Flutter bridge availability
      const hasFlutterBridge = !!(window.flutterBridge && typeof window.flutterBridge.downloadAndSaveFile === 'function')
      console.log('🔧 Has Flutter bridge with downloadAndSaveFile:', hasFlutterBridge)
      
      // Use native bridge for mobile apps
      if (isFlutter && hasFlutterBridge) {
        console.log('✅ Using native bridge for download')
        console.log('🔧 Calling nativeBridge.downloadAndSaveFile with:', documentInfo)
        
        const success = await nativeBridge.downloadAndSaveFile(documentInfo)
        console.log('✅ Native bridge download result:', success)
        
        if (success) {
          console.log('✅ File saved via native bridge - will auto-open')
          return { success: true, message: 'File saved to device storage and opened' }
        } else {
          console.log('❌ Native bridge download failed, falling back to web download')
        }
      } else {
        console.log('❌ Not in Flutter app or bridge not available, using web download')
        console.log('❌ isFlutter:', isFlutter)
        console.log('❌ hasFlutterBridge:', hasFlutterBridge)
      }
      
      // Fallback to web-based download
      console.log('Using web-based download fallback')
      const webResult = this.downloadFileWeb(documentInfo)
      console.log('=== DOWNLOAD DEBUG END ===')
      return webResult
      
    } catch (error) {
      console.error('Download error:', error)
      console.log('=== DOWNLOAD DEBUG END (ERROR) ===')
      return { success: false, error: error.message }
    }
  }

  /**
   * Web-based file download
   */
  async downloadFileWeb(documentInfo) {
    try {
      let downloadUrl = documentInfo.FileUrl
      
      // For non-Firebase storage types, always download from API
      // This ensures we don't open new tabs with direct URLs
      if (documentInfo.storagetype !== 'firebase' || !downloadUrl || this.isLocalApiUrl(downloadUrl)) {
        console.log('Downloading from API endpoint (non-Firebase or local URL)')
        const response = await mobileApiClient.get(`/Documents/DownloadDocumentFile/${documentInfo.Docid}`, {
          responseType: 'blob'
        })
        
        const blob = new Blob([response])
        downloadUrl = URL.createObjectURL(blob)
      }
      
      // Create download link
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = documentInfo.Docname || 'document'
      link.style.display = 'none'
      link.target = '_self'
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up blob URL if created
      if (downloadUrl.startsWith('blob:')) {
        setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000)
      }
      
      return { success: true, message: 'File download initiated' }
      
    } catch (error) {
      console.error('Web download error:', error)
      return { success: false, error: error.message }
    }
  }

  /**
   * Get file content for viewing (for images)
   */
  async getFileContent(documentInfo) {
    try {
      // For non-Firebase storage types, always download from API
      // This ensures consistent behavior across storage types
      if (documentInfo.storagetype !== 'firebase' || !this.isFirebaseUrl(documentInfo.FileUrl)) {
        console.log('Downloading file content from API (non-Firebase storage)')
        const response = await mobileApiClient.get(`/Documents/DownloadDocumentFile/${documentInfo.Docid}`, {
          responseType: 'blob'
        })
        
        const blob = new Blob([response])
        return URL.createObjectURL(blob)
      }
      
      // If Firebase URL, use directly
      return documentInfo.FileUrl
      
    } catch (error) {
      console.error('Get file content error:', error)
      // Return null instead of throwing error to prevent breaking download functionality
      return null
    }
  }

  /**
   * Test download functionality
   */
  async testDownload() {
    try {
      console.log('Testing download functionality...')
      
      // Create a test blob
      const testContent = 'This is a test file for download functionality'
      const blob = new Blob([testContent], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      
      // Test native bridge download
      if (nativeBridge.isRunningInFlutter()) { // Check dynamically
        const result = await nativeBridge.saveFileToDevice(url, 'test-download.txt', 'document')
        console.log('Native bridge test result:', result)
      }
      
      // Test web download
      const link = document.createElement('a')
      link.href = url
      link.download = 'test-download.txt'
      link.style.display = 'none'
      link.target = '_self'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      
      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 1000)
      
      return { success: true, message: 'Test download completed' }
    } catch (error) {
      console.error('Test download error:', error)
      return { success: false, error: error.message }
    }
  }
}

// Create singleton instance
const fileDownloadUtil = new FileDownloadUtil()

export default fileDownloadUtil 